import { useState, useEffect, useCallback } from 'react'
import type { Match } from '@api/useSupplierReconciliation/types/match'
import { useMatchOptionsForInvoice } from '@/api/useMatchOptionsForInvoice'
import { useMatchOptionsForBol } from '@/api/useMatchOptionsForBol'
import { useMatchLink } from '@/api/useSupplierReconciliation/useMatchLink'
import type { InferResponse } from '@/hooks/useApiClient'

// Use API types directly
type BolCandidate = InferResponse<'/supplier_rec/match/options_for_invoice'>[number]
type InvoiceCandidate = InferResponse<'/supplier_rec/match/options_for_bol'>[number]

export interface InlineMatchManagementState {
  isManageMode: boolean
  searchMode: 'bol' | 'invoice'
  selectedIds: string[]
  searchResults: (BolCandidate | InvoiceCandidate)[]
  hasUnsavedChanges: boolean
}

export function useInlineMatchManagement(match: Match | null) {
  // State management
  const [isManageMode, setIsManageMode] = useState(false)
  const [searchMode, setSearchMode] = useState<'bol' | 'invoice'>('bol')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [searchResults, setSearchResults] = useState<(BolCandidate | InvoiceCandidate)[]>([])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [originalSearchMode, setOriginalSearchMode] = useState<'bol' | 'invoice'>('bol')
  const [originalSelectedIds, setOriginalSelectedIds] = useState<string[]>([])

  // Get the first invoice ID from match for API call
  const firstInvoiceId = match?.invoice_numbers?.[0]
    ? typeof match.invoice_numbers[0] === 'object'
      ? match.invoice_numbers[0].id
      : match.invoice_numbers[0]
    : null

  // Get the first BOL ID from match for API call
  const firstBolId = match?.bol_numbers?.[0]
    ? typeof match.bol_numbers[0] === 'object'
      ? match.bol_numbers[0].id
      : match.bol_numbers[0]
    : null

  // API queries
  const bolOptionsEnabled = !!firstInvoiceId && isManageMode && searchMode === 'bol'
  const invoiceOptionsEnabled = !!firstBolId && isManageMode && searchMode === 'invoice'

  const bolOptionsQuery = useMatchOptionsForInvoice(
    { invoice_id: firstInvoiceId || '' },
    {
      enabled: bolOptionsEnabled,
    }
  )

  const invoiceOptionsQuery = useMatchOptionsForBol(
    { bol_id: firstBolId || '' },
    {
      enabled: invoiceOptionsEnabled,
    }
  )

  // Save link mutation
  const linkMutation = useMatchLink()

  // Initialize search mode when entering manage mode
  useEffect(() => {
    if (isManageMode && match) {
      // Determine default search mode based on what's in the match
      const hasBols = match.bol_numbers && match.bol_numbers.length > 0
      const hasInvoices = match.invoice_numbers && match.invoice_numbers.length > 0

      // Logic: If only invoices exist, search for BOLs; if only BOLs exist, search for invoices
      let defaultMode: 'bol' | 'invoice' = 'bol'
      if (hasInvoices && !hasBols) {
        defaultMode = 'bol'
      } else if (hasBols && !hasInvoices) {
        defaultMode = 'invoice'
      } else {
        // Both exist or neither exist - default to BOL search
        defaultMode = 'bol'
      }

      setSearchMode(defaultMode)
      setOriginalSearchMode(defaultMode)
      setOriginalSelectedIds([])
      setSelectedIds([])
      setHasUnsavedChanges(false)
    }
  }, [isManageMode, match, firstBolId, firstInvoiceId])

  // Update search results when API data changes
  useEffect(() => {
    if (searchMode === 'bol' && bolOptionsQuery.data) {
      setSearchResults(bolOptionsQuery.data)
    } else if (searchMode === 'invoice' && invoiceOptionsQuery.data) {
      setSearchResults(invoiceOptionsQuery.data)
    } else if (
      (searchMode === 'bol' && bolOptionsQuery.isError) ||
      (searchMode === 'invoice' && invoiceOptionsQuery.isError)
    ) {
      setSearchResults([])
    }
  }, [bolOptionsQuery.data, bolOptionsQuery.isError, invoiceOptionsQuery.data, invoiceOptionsQuery.isError, searchMode])

  // Track changes for unsaved state
  useEffect(() => {
    const hasChanges =
      searchMode !== originalSearchMode ||
      selectedIds.length !== originalSelectedIds.length ||
      selectedIds.some((id) => !originalSelectedIds.includes(id)) ||
      originalSelectedIds.some((id) => !selectedIds.includes(id))

    setHasUnsavedChanges(hasChanges)
  }, [searchMode, selectedIds, originalSearchMode, originalSelectedIds])

  // Get the existing items we're matching TO
  const getExistingItems = useCallback(() => {
    if (!match) return []

    if (searchMode === 'bol') {
      // Searching for BOLs, so show existing invoices
      return match.invoice_numbers || []
    } else {
      // Searching for invoices, so show existing BOLs
      return match.bol_numbers || []
    }
  }, [match, searchMode])

  // Toggle selection of a match candidate
  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((sid) => sid !== id)
      } else {
        return [...prev, id]
      }
    })
  }, [])

  // Enter manage mode
  const enterManageMode = useCallback(() => {
    setIsManageMode(true)
  }, [])

  // Cancel changes and exit manage mode
  const cancelChanges = useCallback(() => {
    setIsManageMode(false)
    setSelectedIds(originalSelectedIds)
    setSearchMode(originalSearchMode)
    setHasUnsavedChanges(false)
    setSearchResults([])
  }, [originalSelectedIds, originalSearchMode])

  // Save changes and exit manage mode
  const saveChanges = useCallback(async () => {
    if (!match || selectedIds.length === 0) return

    try {
      // Build the PendingDocuments structure based on current selections
      const invoices: Array<{ invoice_number: string; invoice_id?: string | null }> = []
      const bols: Array<{ bol_number: string; bol_id?: string | null }> = []

      if (searchMode === 'bol') {
        // We're matching BOLs to existing invoice(s)
        const selectedBols = searchResults.filter((item) => selectedIds.includes(item.bol_id)) as BolCandidate[]

        // Add existing invoices
        match.invoice_numbers?.forEach((invoice) => {
          if (typeof invoice === 'object') {
            invoices.push({
              invoice_number: invoice.number,
              invoice_id: invoice.id,
            })
          }
        })

        // Add selected BOLs
        selectedBols.forEach((bol) => {
          bols.push({
            bol_number: bol.bol_number,
            bol_id: bol.bol_id,
          })
        })
      } else {
        // We're matching invoices to existing BOL(s)
        const selectedInvoices = searchResults.filter((item) =>
          selectedIds.includes(item.invoice_id)
        ) as InvoiceCandidate[]

        // Add selected invoices
        selectedInvoices.forEach((invoice) => {
          invoices.push({
            invoice_number: invoice.invoice_number,
            invoice_id: invoice.invoice_id,
          })
        })

        // Add existing BOLs
        match.bol_numbers?.forEach((bolNumber) => {
          bols.push({
            bol_number: typeof bolNumber === 'object' ? bolNumber.number : bolNumber,
            bol_id: typeof bolNumber === 'object' ? bolNumber.id : null,
          })
        })
      }

      // Call the API with the correct PendingDocuments structure
      await linkMutation.mutateAsync({
        match_id: match.id,
        links: {
          invoices,
          bols,
        },
      })

      // Success - update original state and exit manage mode
      setOriginalSearchMode(searchMode)
      setOriginalSelectedIds([...selectedIds])
      setIsManageMode(false)
      setHasUnsavedChanges(false)
      setSearchResults([])
    } catch (error) {
      // Error is handled by the mutation, just re-throw for caller to handle
      throw error
    }
  }, [match, selectedIds, searchMode, searchResults, linkMutation])

  // Check if mode switching should be disabled
  const canSwitchMode = useCallback((): boolean => {
    if (!match) return false

    const hasBols = match.bol_numbers && match.bol_numbers.length > 0
    const hasInvoices = match.invoice_numbers && match.invoice_numbers.length > 0

    // Can always switch if we have at least one type
    return hasBols || hasInvoices
  }, [match])

  // Switch search mode
  const switchSearchMode = useCallback(
    (newMode: 'bol' | 'invoice') => {
      if (canSwitchMode()) {
        setSearchMode(newMode)
        setSelectedIds([]) // Clear selections when switching modes
      }
    },
    [canSwitchMode]
  )

  return {
    // State
    isManageMode,
    searchMode,
    selectedIds,
    searchResults,
    hasUnsavedChanges,

    // Computed
    existingItems: getExistingItems(),
    canSwitchMode: canSwitchMode(),

    // Loading states
    isLoading: bolOptionsQuery.isLoading || invoiceOptionsQuery.isLoading,
    isSaving: linkMutation.isPending,
    hasError: bolOptionsQuery.isError || invoiceOptionsQuery.isError,
    error: bolOptionsQuery.error || invoiceOptionsQuery.error,

    // Actions
    enterManageMode,
    cancelChanges,
    saveChanges,
    toggleSelection,
    switchSearchMode,
  }
}

export type { BolCandidate, InvoiceCandidate }
